import { useEffect, useRef, useState } from 'react';
import type { Calendar, EventOccurrence } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { EventParticipation } from '../components/EventParticipation';
import { formatEventRange } from '../format';
import '../components/participation.css';
export function AttendancePage({
  session,
  calendars,
  eventId,
}: {
  session: ClientSession | null;
  calendars: Calendar[];
  eventId?: string;
}) {
  const [calendarId, setCalendarId] = useState(calendars[0]?.id || '');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [events, setEvents] = useState<EventOccurrence[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);
  const load = async () => {
    if (!calendarId) return;
    const version = ++requestVersion.current;
    setLoading(true);
    setEvents([]);
    setError('');
    try {
      const from = new Date(`${date}T00:00:00`);
      const to = new Date(from);
      to.setMonth(to.getMonth() + 3);
      const result = await api.events(from, to, [calendarId]);
      if (version === requestVersion.current) setEvents(result.events);
    } catch (cause) {
      if (version === requestVersion.current) setError((cause as Error).message);
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };
  useEffect(() => {
    if (!calendarId && calendars.length) setCalendarId(calendars[0]!.id);
  }, [calendars, calendarId]);
  useEffect(() => {
    if (!eventId && session?.user && calendarId) void load();
  }, [eventId, session?.user?.id, calendarId]);
  if (!session?.user)
    return (
      <main id="main-content" className="participation-page">
        <h1>Sign in to view attendance</h1>
        <a href="/login">Sign in</a>
      </main>
    );
  return (
    <main id="main-content" className="participation-page">
      <a href="/admin">← Administration</a>
      <h1>Attendance &amp; volunteers</h1>
      <p>
        RSVP names and counts are private to authorized staff and calendar managers. Volunteer lists
        require roster permission.
      </p>
      {eventId ? (
        <>
          <a href="/admin/participation">Choose another event</a>
          <EventParticipation
            mode="report"
            eventId={eventId}
            occurrence={new URLSearchParams(location.search).get('occurrence')}
            session={session}
          />
        </>
      ) : (
        <>
          <form
            className="participation-card"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <label className="field">
              Calendar
              <select
                aria-label="Calendar"
                value={calendarId}
                onChange={(event) => setCalendarId(event.target.value)}
              >
                {calendars.map((calendar) => (
                  <option key={calendar.id} value={calendar.id}>
                    {calendar.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              Events starting from
              <input
                type="date"
                required
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </label>
            <p>Shows the next three months.</p>
            <button className="button" disabled={loading}>
              {loading ? 'Loading…' : 'Find events'}
            </button>
          </form>
          <ul className="participation-event-list">
            {events.map((event) => (
              <li className="participation-card" key={event.occurrenceId}>
                <h2>{event.title}</h2>
                <p>{formatEventRange(event)}</p>
                <a
                  className="button button-quiet"
                  href={`/admin/participation/events/${event.id}${event.recurrenceId ? `?occurrence=${encodeURIComponent(event.recurrenceId)}` : ''}`}
                >
                  View private attendance
                </a>
              </li>
            ))}
          </ul>
          {!loading && !events.length && <p>No events found in this period.</p>}
        </>
      )}
      {error && <p role="alert">{error}</p>}
    </main>
  );
}
